import { createSlice, createAsyncThunk, PayloadAction, isAnyOf } from '@reduxjs/toolkit';
import { AuthState, LoginRequest } from '@/types/residents';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
// Admin RTK Query replaces AdminAuthAPI for login/logout and session checks
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { strings } from '@/constants/residents/strings';
import { User } from '@/types/residents/user';
import type { AvailableContext } from '@/types/auth';
import { AUTH_ERROR_MESSAGES } from '@/constants/shared/auth';
import { authApi } from '@/store/api/authApi';
import { organizationsApi } from '@/store/api/admin/organizationsApi';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  biometricEnabled: false,
  // Unified Auth (RES-89): populated from the login response. `availableContexts`
  // is persisted (list only) so a restored session routes without a re-fetch;
  // `activeContext` is NOT persisted, so the picker re-fires each session.
  availableContexts: [],
  activeContext: null,
};

/**
 * Store the account's available contexts and auto-select the only one when there
 * is exactly one (design §5.1 — single-context users skip the picker). A fresh
 * login always clears any prior choice; more than one context leaves
 * `activeContext` null so `RootNavigator` shows the picker.
 */
const applyLoginContexts = (state: AuthState, contexts: AvailableContext[]): void => {
  state.availableContexts = contexts;
  state.activeContext = contexts.length === 1 ? contexts[0] : null;
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      // Step 1: Authenticate user
      const loginResponse = await AuthAPI.login(credentials.email, credentials.password);
      
      // Step 2: Validate response structure
      if (!loginResponse.success || 
          !loginResponse.response?.user || 
          !loginResponse.response?.accessToken) {
        return rejectWithValue('Invalid login response from server');
      }
      
      const { accessToken } = loginResponse.response;

      // Unified Auth (RES-89): the "residents only" gate (G-3) and the
      // client-side valid-lease gate are removed. Any account the backend
      // authenticates is welcome; the returned `availableContexts` drive the
      // picker/route, and backend authorization still enforces access per
      // endpoint. We only persist the access token so authenticated requests
      // (and the relaunch session restore) work.
      await SecureStorage.getInstance().saveToken(accessToken);

      return loginResponse;
    } catch (error: any) {
      // Handle different error types
      if (error.response?.data?.message) {
        return rejectWithValue(error.response.data.message);
      }
      
      if (error.code) {
        return rejectWithValue(error.message || 'Authentication failed');
      }
      
      const errorMessage = error.message || strings.error.generic;
      return rejectWithValue(errorMessage);
    }
  }
);

// Admin login async thunk
// Removed legacy adminLogin thunk in favor of RTK Query useAdminLoginMutation

// Admin logout async thunk
// Removed legacy adminLogout thunk; handled in useAdminScreen via RTK Query

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await AuthAPI.logout();
      await SecureStorage.getInstance().clearTokens();
    } catch (error: any) {
      // Logout should always succeed locally even if API fails
      console.warn('Logout API call failed:', error);
      await SecureStorage.getInstance().clearTokens();
    }
  }
);

export const refreshUser = createAsyncThunk(
  'auth/refreshUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await AuthAPI.getCurrentUser();
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh user data');
    }
  }
);

// Validate session with server (use this when you need to actually check with server)
export const validateSession = createAsyncThunk(
  'auth/validateSession',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = await SecureStorage.getInstance().getToken();
      if (!token) {
        return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
      }
      
      const state = getState() as any;
      const user = state.auth.user;
      
      // Make server call to validate session
      if (user?.roles && user.roles.length > 0) {
        const adminUser = await AuthAPI.getCurrentUser();
        return adminUser;
      } else {
        const residentUser = await AuthAPI.getCurrentUser();
        return residentUser;
      }
    } catch (error: any) {
      const errorCode = error?.code || '';
      if (errorCode === '401' || errorCode === '403') {
        return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
      }
      return rejectWithValue(strings.error.generic);
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkStatus',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = await SecureStorage.getInstance().getToken();
      if (!token) {
        return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
      }
      
      const state = getState() as any;
      const user = state.auth.user;
      
      // Follow admin-web pattern: if we have a token and persisted user, consider authenticated
      // Don't make server calls on startup to avoid triggering logout on token expiry
      if (user && token) {
        return user;
      }
      
      // If no persisted user but we have a token, reject to force re-login
      return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
    } catch (error: any) {
      return rejectWithValue(AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN);
    }
  }
);

// Enhanced session validation for admin users
// Removed legacy validateAdminSession; session is validated via checkAuthStatus -> AuthAPI.getCurrentUser

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setBiometricEnabled: (state, action: PayloadAction<boolean>) => {
      state.biometricEnabled = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    // Unified Auth (RES-89): the picker taps this to choose a context; the
    // component also flushes RTK caches so the other context's data can't leak.
    setActiveContext: (state, action: PayloadAction<AvailableContext>) => {
      state.activeContext = action.payload;
    },
    clearActiveContext: (state) => {
      state.activeContext = null;
    },
    resetAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.biometricEnabled = false;
      state.availableContexts = [];
      state.activeContext = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.response!.user;
        state.isAuthenticated = true;
        state.error = null;
        // Unified Auth (RES-89): the server is authoritative at login.
        applyLoginContexts(state, action.payload.response!.availableContexts ?? []);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Login failed';
        state.isAuthenticated = false;
        state.user = null;
        state.availableContexts = [];
        state.activeContext = null;
      });
    
    // Admin login/logout handled via RTK Query mutations and resetAuth reducer
    
    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
        state.biometricEnabled = false;
        state.availableContexts = [];
        state.activeContext = null;
      })
      .addCase(logout.rejected, (state) => {
        // Even if logout fails, clear local state
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.biometricEnabled = false;
        state.availableContexts = [];
        state.activeContext = null;
      });
    
    // Refresh User
    builder
      .addCase(refreshUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(refreshUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to refresh user data';
      });
    
    // Check Auth Status (local check only)
    builder
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        // Unified Auth (RES-89): `availableContexts` is persisted, so a
        // token-restored relaunch already has it rehydrated here — nothing to
        // fetch or re-derive. The single-context re-selection happens through
        // the `restoreActiveContext` thunk (dispatched by RootNavigator) so the
        // adminUI extraReducer initializes; multi-context sessions keep
        // `activeContext` null and re-show the picker (DC4).
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload as string || 'Authentication check failed';
      });
    
    // Validate Session (server check)
    builder
      .addCase(validateSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(validateSession.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload as string || 'Session validation failed';
      });
    
    // RTK Query Matchers - Handle successful login/admin login/Google login/Apple login
    // This matches admin-web's pattern for automatic state management after login
    builder
      .addMatcher(
        isAnyOf(
          authApi.endpoints.adminLogin.matchFulfilled,
          authApi.endpoints.googleLogin.matchFulfilled,
          authApi.endpoints.appleLogin.matchFulfilled
        ),
        (state, action) => {
          const { payload } = action;
          
          // Validate response structure
          if (!payload?.response?.user) {
            console.error('Invalid login response structure:', payload);
            return;
          }
          
          // Update auth state with user data
          state.isLoading = false;
          state.user = payload.response.user;
          state.isAuthenticated = true;
          state.error = null;
          // Unified Auth (RES-89): populate contexts for the admin/OAuth login
          // paths too, so the picker/route logic is fed on every login.
          applyLoginContexts(state, payload.response.availableContexts ?? []);
        }
      )
      .addMatcher(
        isAnyOf(
          authApi.endpoints.adminLogin.matchRejected,
          authApi.endpoints.googleLogin.matchRejected,
          authApi.endpoints.appleLogin.matchRejected
        ),
        (state) => {
          state.isLoading = false;
          state.isAuthenticated = false;
          state.user = null;
          state.availableContexts = [];
          state.activeContext = null;
        }
      )
      // Listen for organization updates and sync to user roles
      .addMatcher(
        organizationsApi.endpoints.putOrganization.matchFulfilled,
        (state, action) => {
          // Update organization name in user's roles
          const updatedOrg = action.payload?.response;
          if (updatedOrg && state.user?.roles) {
            state.user.roles = state.user.roles.map((role: any) => {
              if (role.organization?.id === updatedOrg.id) {
                return {
                  ...role,
                  organization: {
                    ...role.organization,
                    name: updatedOrg.name,
                  }
                };
              }
              return role;
            });
          }
        }
      );
  },
});

export const {
  clearError,
  setBiometricEnabled,
  setUser,
  setActiveContext,
  clearActiveContext,
  resetAuth,
} = authSlice.actions;

/**
 * Unified Auth (RES-89) — relaunch context re-selection.
 *
 * `availableContexts` is persisted, so a token-restored session already has the
 * authoritative list rehydrated; `activeContext` is NOT persisted, so it starts
 * null. When the account has exactly one context we re-select it here — through
 * the `setActiveContext` ACTION (not a bare mutation) so the adminUI reducer
 * initializes `selectedOrganizationId` (critical because a plain-login persisted
 * user may carry no `roles` for `useAdminUser` to fall back on). More than one
 * context stays unselected, so `RootNavigator` re-shows the picker each session.
 *
 * Dispatched by `RootNavigator` right after `checkAuthStatus` resolves — i.e.
 * wherever the app first reads a restored session. It is a no-op after login
 * (the login flow already set `activeContext`).
 */
export const restoreActiveContext = () => (dispatch: any, getState: any): void => {
  const { availableContexts, activeContext, isAuthenticated } = (getState() as {
    auth: AuthState;
  }).auth;
  if (isAuthenticated && !activeContext && (availableContexts?.length ?? 0) === 1) {
    dispatch(setActiveContext(availableContexts[0]));
  }
};

export default authSlice.reducer;